import React, { FC } from 'react';
import * as Styled from '../../ScoreOverview.styled';
import ScorePercentCircle from '@core/components/Analytics/VirtualHuman/ScoreOverview/components/ScorePercentCircle/ScorePercentCircle';
import { GetFormattedTime } from '@base/utility/TimeFormatting';
import AnalyticsSummaryItem from '@core/components/Analytics/VirtualHuman/ScoreOverview/components/AnalyticsSummaryItem/AnalyticsSummaryItem';
import { ToDecimalPlace } from '@base/utility/NumberFormatting';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useIsV3VH } from '@base/utility/VHHooks/useIsV3VH';

/**
 * @type {FC<{
 * hideScores?: boolean;
 * pointsScored: number;
 * clampedScorePercentageFraction: number;
 * scorePercentage: number;
 * timeSpent: number;
 * vhType: import('@core/models/virtualhuman.types').VHType;
 * pointsPossible: number;
 * }>}
 */
const ScoreStats = ({
  hideScores,
  pointsScored,
  clampedScorePercentageFraction,
  scorePercentage,
  timeSpent,
  vhType,
  pointsPossible,
}) => {
  const isV3VH = useIsV3VH(vhType);
  const showMainScore =
    (!hideScores && pointsPossible > 0) ||
    (isV3VH && typeof scorePercentage === 'number');
  const showObjectivePoints = !isV3VH && pointsPossible > 0;
  const { t } = useTranslation();

  return (
    <>
      {showMainScore && (
        <Styled.SummaryCircle>
          <ScorePercentCircle
            scorePercent={ToDecimalPlace(clampedScorePercentageFraction, 1)}
          />
          <Styled.AbsoluteSummaryItem>
            <AnalyticsSummaryItem
              label={t('score')}
              value={`${scorePercentage}%`}
              iconName="medal"
            />
          </Styled.AbsoluteSummaryItem>
        </Styled.SummaryCircle>
      )}
      <Styled.BlueSummaryCircle>
        <AnalyticsSummaryItem
          label={t('timeTaken')}
          value={GetFormattedTime(timeSpent)}
          iconName="clock"
        />
      </Styled.BlueSummaryCircle>
      {showObjectivePoints && (
        <Styled.TurquoiseSummaryCircle>
          <AnalyticsSummaryItem
            label={t('objectivePoints')}
            value={`${pointsScored}`}
            iconName="diamond"
          />
        </Styled.TurquoiseSummaryCircle>
      )}
    </>
  );
};

ScoreStats.propTypes = {
  hideScores: PropTypes.bool,
  pointsScored: PropTypes.number.isRequired,
  clampedScorePercentageFraction: PropTypes.number.isRequired,
  scorePercentage: PropTypes.number.isRequired,
  timeSpent: PropTypes.number.isRequired,
  vhType:
    /** @type {import('prop-types').Validator<import('@core/models/virtualhuman.types').VHType>}*/ (
      PropTypes.oneOf([
        'medical',
        'default',
        'branching',
        'freeform_v3',
        'medical_v3',
      ]).isRequired
    ),
  pointsPossible: PropTypes.number.isRequired,
};

export default ScoreStats;
